import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import AuthCallback from "@/pages/AuthCallback";
import { useAuth } from "@/context/AuthContext";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { motion } from "framer-motion";
import { isHalloween, isChristmas } from "@/lib/season";
import { setReferral } from "@/lib/referral";
import ReferralBanner from "@/components/ReferralBanner";

export function Nav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // lock the page behind the open menu so mobile visitors can't scroll the background
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  // seasonal dress-up: October haunts, December snows — both retire themselves
  useEffect(() => {
    document.documentElement.classList.toggle("halloween", isHalloween());
    document.documentElement.classList.toggle("christmas", isChristmas());
  }, []);

  // referral capture: /r/{house} bounces here with ?ref= — hold it until signup
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) setReferral(ref);
  }, [location.search]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const links = [
    ["/", "Home", "home"],
    ["/models", "Talent Board", "models"],
    ["/castings", "Casting Calls", "castings"],
    ...(user ? [["/dashboard", "Edit Profile", "dashboard"]] : []),
  ];

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/85 backdrop-blur-md border-b border-white/10">
        <div className="flex items-center justify-between px-6 sm:px-10 h-16">
          <Link to="/" data-testid="nav-brand" className="flex items-center gap-3">
            <img src="/solis-logo.jpg" alt="Solis Casting logo" className="h-9 w-9 object-cover border border-white/15" data-testid="nav-logo" />
            <span className="font-display text-2xl tracking-tight">Solis <span className="text-accent italic">Casting</span></span>
          </Link>
          <nav className="hidden md:flex items-center gap-10">
            {links.map(([to, label, key]) => (
              <Link key={key} to={to} data-testid={`nav-${key}`} className="overline-label !font-semibold !text-white/85 hover:text-foreground transition-colors duration-300">
                {label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-4">
            {user === undefined ? null : user ? (
              <>
                <span className="hidden sm:block font-grotesk font-semibold text-xs uppercase tracking-[0.15em] text-white/85" data-testid="nav-user-name">
                  {user.name}
                </span>
                <button data-testid="nav-logout-button" onClick={handleLogout} className="btn-outline !px-5 !py-2.5">
                  Sign out
                </button>
              </>
            ) : (
              <Link to="/auth" data-testid="nav-login-button" className="btn-outline !px-5 !py-2.5">
                Sign in
              </Link>
            )}
            <button data-testid="nav-menu-button" onClick={() => setMenuOpen(true)} className="md:hidden btn-outline !px-4 !py-2.5" aria-label="Open menu">
              <Menu size={16} />
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[60] bg-background flex flex-col md:hidden"
          data-testid="mobile-nav"
        >
          <div className="flex items-center justify-between px-6 h-16 border-b border-white/10">
            <span className="font-display text-2xl tracking-tight">Solis <span className="text-accent italic">Casting</span></span>
            <button data-testid="nav-menu-close" onClick={() => setMenuOpen(false)} className="btn-outline !px-4 !py-2.5" aria-label="Close menu">
              <X size={16} />
            </button>
          </div>
          <nav className="flex flex-col px-6 pt-10">
            {links.map(([to, label, key], i) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.08, duration: 0.4 }}
              >
                <Link
                  to={to}
                  data-testid={`mobile-nav-${key}`}
                  onClick={() => setMenuOpen(false)}
                  className="block font-display font-light text-5xl tracking-tighter py-4 border-b border-white/10 hover:text-accent transition-colors"
                >
                  {label}
                </Link>
              </motion.div>
            ))}
          </nav>
          <div className="mt-auto px-6 pb-10 flex flex-col gap-8">
            {user ? (
              <button data-testid="mobile-nav-logout" onClick={handleLogout} className="btn-outline w-full !py-3.5">
                Sign out
              </button>
            ) : (
              <Link to="/auth" data-testid="mobile-nav-join" onClick={() => setMenuOpen(false)} className="btn-primary w-full !py-3.5 inline-flex items-center justify-center gap-2">
                Sign in / Join the board <ArrowUpRight size={14} />
              </Link>
            )}
            <div className="flex gap-8">
              <Link to="/about" data-testid="mobile-nav-about" className="font-grotesk font-medium text-xs uppercase tracking-[0.2em] text-white/60 hover:text-foreground transition-colors">About</Link>
              <Link to="/contact" data-testid="mobile-nav-contact" className="font-grotesk font-medium text-xs uppercase tracking-[0.2em] text-white/60 hover:text-foreground transition-colors">Contact</Link>
              <a href="https://instagram.com/Soliscasting" target="_blank" rel="noopener noreferrer" data-testid="mobile-nav-instagram" className="font-grotesk font-medium text-xs uppercase tracking-[0.2em] text-white/60 hover:text-foreground transition-colors">Instagram</a>
            </div>
          </div>
        </motion.div>
      )}
    </>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-white/10">
      <div className="py-6 border-b border-white/10">
        <Marquee speed={35} gradient={false}>
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className="font-display text-3xl sm:text-4xl mx-8 text-white/70 italic">
              Solis Casting — the casting venue <span className="text-accent not-italic">·</span> Paris
              <span className="text-accent not-italic"> · </span>New York<span className="text-accent not-italic"> · </span>Milan
              <span className="text-accent not-italic"> · </span>London
            </span>
          ))}
        </Marquee>
      </div>
      <div className="px-6 sm:px-10 py-16 grid grid-cols-1 md:grid-cols-3 gap-12">
        <div>
          <div className="flex items-center gap-4 mb-4">
            <img src="/solis-logo.jpg" alt="Solis Casting logo" className="h-12 w-12 object-cover border border-white/15" data-testid="footer-logo" />
            <p className="font-display text-4xl">
              Solis <span className="text-accent italic">Casting</span>
              <svg className="halloween-on halloween-pumpkin inline-block align-[-2px] ml-3" data-testid="halloween-pumpkin" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
                <path d="M12 5.5 C7 5.5 4 9 4 13.5 C4 18 7.5 21 12 21 C16.5 21 20 18 20 13.5 C20 9 17 5.5 12 5.5 Z" />
                <path d="M12 5.5 C10 5.5 8.6 9 8.6 13.5 C8.6 18 10 21 12 21" />
                <path d="M12 5.5 C14 5.5 15.4 9 15.4 13.5 C15.4 18 14 21 12 21" />
                <path d="M12 5.5 C12 3.5 13 2.5 15 2" />
              </svg>
              <svg className="christmas-on christmas-holly inline-block align-[-2px] ml-3" data-testid="christmas-holly" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
                <path d="M6 4 C3 7 3 10 6 12 C4 14 4 17 7 19 C8 16 10 15 12 15 C10 13 10 10 12 8 C9 8 7 6 6 4 Z" />
                <path d="M18 4 C21 7 21 10 18 12 C20 14 20 17 17 19 C16 16 14 15 12 15 C14 13 14 10 12 8 C15 8 17 6 18 4 Z" />
                <circle cx="10.8" cy="19.5" r="1.3" fill="currentColor" stroke="none" />
                <circle cx="13.4" cy="19.8" r="1.3" fill="currentColor" stroke="none" />
                <circle cx="12.1" cy="17.4" r="1.3" fill="currentColor" stroke="none" />
              </svg>
            </p>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
            Where houses, magazines and brands meet their next face. Applications to bookings, in one atelier.
          </p>
        </div>
        <div>
          <p className="overline-label mb-6">For models</p>
          <div className="flex flex-col gap-3">
            <Link to="/auth?mode=register&role=model" data-testid="footer-apply-model" className="text-sm hover:text-accent transition-colors inline-flex items-center gap-2">
              Apply as a model <ArrowUpRight size={14} />
            </Link>
            <Link to="/portfolio-guide" data-testid="footer-guide-link" className="text-sm hover:text-accent transition-colors inline-flex items-center gap-2">
              The portfolio standard <ArrowUpRight size={14} />
            </Link>
            <Link to="/castings" className="text-sm hover:text-accent transition-colors inline-flex items-center gap-2">
              Browse casting calls <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
        <div>
          <p className="overline-label mb-6">For brands</p>
          <div className="flex flex-col gap-3">
            <Link to="/auth?mode=register&role=employer" data-testid="footer-employer" className="text-sm hover:text-accent transition-colors inline-flex items-center gap-2">
              Post a casting <ArrowUpRight size={14} />
            </Link>
            <Link to="/casting-guide" data-testid="footer-casting-guide-link" className="text-sm hover:text-accent transition-colors inline-flex items-center gap-2">
              The casting standard <ArrowUpRight size={14} />
            </Link>
            <Link to="/models" className="text-sm hover:text-accent transition-colors inline-flex items-center gap-2">
              Scout the talent board <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </div>
      <div className="px-6 sm:px-10 py-12 border-t border-white/10" data-testid="site-disclaimer">
        <div className="flex flex-wrap gap-8 mb-8">
          <Link to="/about" data-testid="footer-about-link" className="font-grotesk font-medium text-xs uppercase tracking-[0.2em] text-white/70 hover:text-foreground transition-colors">
            About Us
          </Link>
          <Link to="/contact" data-testid="footer-contact-link" className="font-grotesk font-medium text-xs uppercase tracking-[0.2em] text-white/70 hover:text-foreground transition-colors">
            Contact Us
          </Link>
          <Link to="/terms" data-testid="footer-terms-link" className="font-grotesk font-medium text-xs uppercase tracking-[0.2em] text-white/70 hover:text-foreground transition-colors">
            Terms of Service
          </Link>
          <Link to="/privacy" data-testid="footer-privacy-link" className="font-grotesk font-medium text-xs uppercase tracking-[0.2em] text-white/70 hover:text-foreground transition-colors">
            Privacy Policy
          </Link>
          <Link to="/refund" data-testid="footer-refund-link" className="font-grotesk font-medium text-xs uppercase tracking-[0.2em] text-white/70 hover:text-foreground transition-colors">
            Refund Policy
          </Link>
          <Link to="/affiliate-policy" data-testid="footer-affiliate-link" className="font-grotesk font-medium text-xs uppercase tracking-[0.2em] text-white/70 hover:text-foreground transition-colors">
            Affiliate Policy
          </Link>
        </div>
        <p className="text-sm font-medium text-white/70 leading-relaxed max-w-5xl mb-8">
          Disclaimer: Solis Casting is not a talent agency, employer or a talent scout; the site is only a venue. Solis Casting does not promise or facilitate employment. The number of casting calls available varies by location, roles available and the level of experience required. As with any business, results may vary, and will be based on individual capacity, experience, expertise, and level of desire. There are no guarantees concerning the level of income the user may experience.
        </p>
        <p className="text-sm font-medium text-white/70 leading-relaxed max-w-5xl">
          Trademark Disclaimer: Any product names, logos, brands, and other trademarks or images featured or referred to within the Soliscasting.com website are the property of their respective trademark holders. These trademark holders are not affiliated with Soliscasting.com, our services, or our websites. They do not sponsor or endorse Soliscasting.com or any of our online products.
        </p>
      </div>
      <div className="px-6 sm:px-10 py-6 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-3">
        <p className="font-grotesk text-xs uppercase tracking-[0.2em] text-muted-foreground">Copyright of Advent Studios.LLC 2011-2026 New York, NY 10012</p>
        <p className="font-grotesk text-xs uppercase tracking-[0.2em] text-muted-foreground">Est. MMXXVI</p>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Nav />
      <main className="pt-16">
        <ReferralBanner />
        {children}
      </main>
      <Footer />
    </div>
  );
}
